import { createContext, useContext, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { signIn as signInApi, logout as logoutApi } from "../api/authApi";
import { setSessionExpiryHandler } from "../api/apiConfig";
import { useAuthQuery } from "../hooks/useAuthQuery";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user, isLoading: loading, error, refetch } = useAuthQuery();
  
  const isAuthenticated = !!user && !error;

  // Handle session expiration (called by axios interceptor)
  const handleSessionExpiry = useCallback(() => {
    // Invalidate auth query and clear cache
    queryClient.invalidateQueries({ queryKey: ['auth'] });
    queryClient.clear();
    navigate("/signin");
  }, [navigate, queryClient]);

  // Set the session expiry handler for axios interceptor on mount
  useEffect(() => {
    setSessionExpiryHandler(handleSessionExpiry);
  }, [handleSessionExpiry]);

  const signIn = async (credentials) => {
    try {
      const response = await signInApi(credentials);
      
      // Invalidate auth query to trigger refetch with new user data
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      
      // Always navigate to dashboard - let the app handle verification state
      navigate("/dashboard");
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all React Query cache data
      queryClient.clear();
      navigate("/signin");
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    signIn,
    logout,
    refetch,
    handleSessionExpiry,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};